import { useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import "@plannel/pages/factory-plan/common/style/ColorPickerRenderer.scss";

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_COLOR = "#000000";

const normalizeHexColor = (value) => {
  if (isEmpty(value)) return DEFAULT_COLOR;
  const lower = value.toLowerCase();
  return HEX_COLOR_PATTERN.test(lower) ? lower : DEFAULT_COLOR;
};

// 컬럼의 AG Grid 편집모드 여부(colDef.editable)와는 별개다 — colDef.editable은 반드시
// false로 둬서 더블클릭 시 AG Grid 기본 텍스트 편집기가 뜨는 것 자체를 막는다(이 렌더러가
// 유일한 편집 UI). 이 렌더러 자체를 조건부로 잠그고 싶을 때만 cellRendererParams.editable을 쓴다
// (CodePickerRenderer의 cellRendererParams 관례와 동일). 지정 없으면 항상 활성.
const isEditable = (params) => {
  const { editable } = params.cellRendererParams ?? {};
  if (editable === undefined) return true;
  return (typeof editable === "function" && editable(params)) || editable === true;
};

/**
 * 네이티브 input[type=color]로 색상 값을 표시/편집.
 * (레거시 wingui-core의 setColorPickerRenderer(jquery-minicolors) 대응 — swatch 프리셋/
 * hue 슬라이더 같은 장식적 UI는 유보하고 값 자체만 편집 가능하게 이관)
 *
 * 피커 조작 중(onChange)에는 로컬 state만 갱신하고 node.setDataValue를 호출하지 않는다.
 * 매번 그리드에 반영하면 셀이 다시 그려지며 이 input DOM 노드가 재생성되고, 그 순간
 * 브라우저 네이티브 피커 팝업이(자신을 띄운 엘리먼트가 사라지므로) 즉시 닫힌다.
 * blur(다른 곳 클릭)에서만 최종값을 커밋해 피커가 열려있는 동안은 자유롭게 색을 고를 수 있게 한다.
 *
 * 사용: columnDef에 `editable: false`(AG Grid 자체 편집모드 차단 — 필수) +
 * `cellRenderer: ColorPickerRenderer`. 조건부로 잠그려면 `cellRendererParams: { editable: ... }`.
 */
const ColorPickerRenderer = (params) => {
  const [color, setColor] = useState(() => normalizeHexColor(params.value));
  const committedRef = useRef(color);

  useEffect(() => {
    const next = normalizeHexColor(params.value);
    committedRef.current = next;
    setColor(next);
  }, [params.value]);

  if (params.node?.rowPinned) return null;

  const editable = isEditable(params);

  const onChange = (e) => {
    setColor(e.target.value);
  };

  const onBlur = () => {
    if (color !== committedRef.current) {
      committedRef.current = color;
      params.node.setDataValue(params.column, color);
    }
  };

  return (
    <input
      type="color"
      value={color}
      onChange={onChange}
      onBlur={onBlur}
      disabled={!editable}
      className="fp-color-picker"
    />
  );
};

export default ColorPickerRenderer;
